import { Alert, Box, Button, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import ResponsiveDialog from 'app/shared-components/layout/dialog/ResponsiveDialog';
import ResponsiveDialogActions from 'app/shared-components/layout/dialog/ResponsiveDialogActions';
import { sentryMask } from 'sentryReplayPrivacy';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { acumaticaConnectionInitiate } from 'app/services/erp-connection.service';

interface AcumaticaConnectModalProps {
  open: boolean;
  paidolId: string;
  onSuccess: () => void;
  onClose: () => void;
  // 'onboarding' routes the post-OAuth callback back to the onboarding stepper
  // (via /review/onboard) instead of the settings connectivity page. SEE-1175.
  redirectOrigin?: string;
}

// Acumatica instance URL — a host (optionally with a path, e.g.
// https://acme.acumatica.com/CompanyERP). Scheme is optional here; the backend
// forces https. Just enough to catch obvious typos before the round-trip.
const INSTANCE_URL_PATTERN = /^(https?:\/\/)?[a-z0-9.-]+\.[a-z]{2,}(\/\S*)?$/i;

/**
 * SEE-1175 — Acumatica direct-connection connect modal.
 *
 * Acumatica is per-tenant: the customer registers a Connected Application in
 * their own Acumatica instance and supplies its instance URL + Client ID +
 * Client Secret. On submit the backend stashes those on the OAuth state record
 * and returns the instance's authorize URL, and we redirect the browser there
 * to complete OAuth. The callback returns to the connections page with
 * ?acumatica_auth=success|error.
 */
function AcumaticaConnectModal({
  open,
  paidolId,
  onSuccess,
  onClose,
  redirectOrigin,
}: AcumaticaConnectModalProps) {
  const { t } = useTranslation();
  const [instanceUrl, setInstanceUrl] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  // Optional extended web-service endpoint (SM207060) — only for customers who
  // published entities outside the stock Default endpoint (e.g. Branch,
  // Department). Name + version go together; blank uses Default.
  const [endpointName, setEndpointName] = useState('');
  const [apiVersion, setApiVersion] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const normalizedUrl = instanceUrl.trim();
  const urlInvalid = normalizedUrl.length > 0 && !INSTANCE_URL_PATTERN.test(normalizedUrl);
  // A custom endpoint needs BOTH its name and version — one without the other is
  // invalid (the version isn't Default's when the endpoint isn't Default).
  const endpointPartial = !!endpointName.trim() !== !!apiVersion.trim();
  // client secret is submitted untrimmed (secrets can contain spaces); trim only
  // gates whitespace-only input. The backend validates the credentials via the
  // real OAuth exchange, so a wrong value surfaces there.
  const canSubmit =
    !submitting &&
    INSTANCE_URL_PATTERN.test(normalizedUrl) &&
    !!clientId.trim() &&
    !!clientSecret.trim() &&
    !endpointPartial;

  const resetAndClose = () => {
    setInstanceUrl('');
    setClientId('');
    setClientSecret('');
    setEndpointName('');
    setApiVersion('');
    setError(null);
    setSubmitting(false);
    onClose();
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const { authUrl } = await acumaticaConnectionInitiate(
        paidolId,
        normalizedUrl,
        clientId.trim(),
        clientSecret,
        redirectOrigin,
        endpointName.trim() || undefined,
        apiVersion.trim() || undefined
      );
      // Clear the credentials from React state before handing off — the redirect
      // doesn't need them, and on the onboarding path (where the modal stays
      // mounted) or a failed redirect, reopening must not pre-fill the old secret.
      setClientSecret('');
      setClientId('');
      // Let the parent close the modal, then send the browser to Acumatica to
      // authorize. The callback redirects back with ?acumatica_auth=success.
      onSuccess();
      window.location.href = authUrl;
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : t('erpConnection.acumaticaConnectionFailed', 'Failed to connect to Acumatica')
      );
      setSubmitting(false);
    }
  };

  return (
    <ResponsiveDialog
      {...sentryMask}
      open={open}
      onClose={() => !submitting && resetAndClose()}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{t('erpConnection.acumaticaConnectTitle', 'Connect Acumatica')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" mb={2}>
          {t(
            'erpConnection.acumaticaConnectDescription',
            'Enter your Acumatica instance URL and the Client ID and Client Secret from the Connected Application your Acumatica admin created. You will be redirected to Acumatica to authorize access.'
          )}
        </Typography>
        <Box display="flex" flexDirection="column" gap={2}>
          <TextField
            label={t('erpConnection.acumaticaInstanceUrl', 'Instance URL')}
            placeholder="https://your-company.acumatica.com"
            value={instanceUrl}
            onChange={(e) => setInstanceUrl(e.target.value)}
            error={urlInvalid}
            helperText={
              urlInvalid
                ? t(
                    'erpConnection.acumaticaInstanceUrlInvalid',
                    'Enter your Acumatica instance web address (e.g. https://your-company.acumatica.com)'
                  )
                : ' '
            }
            inputProps={{ autoComplete: 'off' }}
            fullWidth
            disabled={submitting}
          />
          <TextField
            label={t('erpConnection.acumaticaClientId', 'Client ID')}
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            inputProps={{ autoComplete: 'off' }}
            fullWidth
            disabled={submitting}
          />
          <TextField
            label={t('erpConnection.acumaticaClientSecret', 'Client Secret')}
            type="password"
            value={clientSecret}
            onChange={(e) => setClientSecret(e.target.value)}
            inputProps={{ autoComplete: 'new-password' }}
            fullWidth
            disabled={submitting}
          />
          <Typography variant="caption" color="text.secondary">
            {t(
              'erpConnection.acumaticaEndpointAdvanced',
              'Advanced — only if your Acumatica admin published a custom web service endpoint (e.g. to expose Branches). Leave blank to use the default endpoint.'
            )}
          </Typography>
          <TextField
            label={t('erpConnection.acumaticaEndpointName', 'Endpoint Name (optional)')}
            placeholder="Default"
            value={endpointName}
            onChange={(e) => setEndpointName(e.target.value)}
            error={endpointPartial}
            helperText={
              endpointPartial
                ? t(
                    'erpConnection.acumaticaEndpointBothRequired',
                    'Endpoint name and version must be provided together'
                  )
                : ' '
            }
            inputProps={{ autoComplete: 'off' }}
            fullWidth
            disabled={submitting}
          />
          <TextField
            label={t('erpConnection.acumaticaEndpointVersion', 'Endpoint Version (optional)')}
            placeholder="20.200.001"
            value={apiVersion}
            onChange={(e) => setApiVersion(e.target.value)}
            error={endpointPartial}
            inputProps={{ autoComplete: 'off' }}
            fullWidth
            disabled={submitting}
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <ResponsiveDialogActions>
        <Button onClick={resetAndClose} disabled={submitting}>
          {t('cancel')}
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!canSubmit}>
          {submitting ? t('loading') : t('erpConnection.acumaticaConnect', 'Connect')}
        </Button>
      </ResponsiveDialogActions>
    </ResponsiveDialog>
  );
}

export default AcumaticaConnectModal;
